import { inject, Injectable } from '@angular/core';
import { from, tap } from 'rxjs';

import {
  getMutations,
  getResourceCollection
} from '@/core/utils/async-resources';
import { BaseRepository } from '@/core/services/base-repository';
import { ApiService } from '@/core/api.service';
import {
  Group,
  GroupRequestCreate,
  GroupRequestUpdate,
  GroupSearchParams
} from '../models/group';
import { SessionService } from '@/core/session.service';

/**
 * Repository that encapsulates all data access required by the demo entity feature.
 *
 * @remarks
 * The class uses the shared async repository helpers to expose signals that
 * components can bind to without manually handling HTTP state.
 */
@Injectable({
  providedIn: 'root'
})
export class GroupRepository extends BaseRepository {
  // TODO: Replace with real http client
  private readonly apiService = inject(ApiService);
  private readonly sessionService = inject(SessionService);

  constructor() {
    super();
  }

  /**
   * @description Define las mutaciones CRUD para grupos
   * @returns {ReturnType<typeof getMutations>} Objeto con create, update y delete
   */
  public mutations() {
    return getMutations({
      create: (data: GroupRequestCreate) =>
        from(
          this.apiService.call({
            microServiceName: 'WSMPGroupManagement',
            resourcePath: '/group/register-group',
            method: 'POST',
            useToken: true,
            body: data,
            bodyEncrypted: true
          })
        ).pipe(
          tap(() =>
            this.notifyChange('create', [String(data.group.sequence)])
          )
        ),
      update: (data: GroupRequestUpdate) =>
        from(
          this.apiService.call({
            microServiceName: 'WSMPGroupManagement',
            resourcePath: '/group/edit-group',
            method: 'PUT',
            useToken: true,
            body: this.mapData(data),
            bodyEncrypted: true
          })
        ).pipe(
          tap(() =>
            this.notifyChange('update', [String(data.group.sequence)])
          )
        ),
      delete: (id: string) =>
        from(
          this.apiService.call({
            microServiceName: 'WSMPGroupManagement',
            resourcePath: `/group/delete-group/{lenderId}/${id}`,
            method: 'DELETE',
            useToken: true,
            pathParams: {
              lenderId: {
                value: this.sessionService.settings()?.lenderId,
                encrypted: true
              }
            }
          })
        ).pipe(tap(() => this.notifyChange('delete', [id])))
    });
  }

  /**
   * @description Obtiene la colección de grupos con búsqueda paginada
   * @returns {ReturnType<typeof getResourceCollection>} Colección reactiva de grupos
   */
  public findBy() {
    return getResourceCollection<GroupSearchParams, Group[]>(
      async (searchParams: GroupSearchParams) => {
        const response = await this.apiService.call({
          microServiceName: 'WSMPGroupManagement',
          resourcePath: `/group/get-groups-by-lenderId/{lenderId}/${searchParams.pageLimit}/${searchParams.pageNumber}`,
          method: 'GET',
          useToken: true,
          pathParams: {
            lenderId: {
              value: searchParams.lenderId,
              encrypted: true
            }
          }
        });
        const data = (response.data.map((item: any) => {
          item.sequence = item.sequential;
          delete item.sequential;
          return item;
        }) ?? []) as Group[];
        return data;
      }
    );
  }

  /**
   * @description Mapea los datos del grupo para la API (sequence → sequential)
   * @param {any} data - Datos a mapear
   * @returns {Group[]} Datos formateados para la API
   * @private
   */
  private mapData(data: any): Group[] {
    data.group.sequential = data.group.sequence;
    delete data.group.sequence;
    return data;
  }
}
